<template>
  <div v-if="packages.length > 0" class="w-full block px-4 md:px-2" data-testid="package-picker">
    <div class="mt-4">
      <span class="group inline-flex rounded-md shadow-sm text-gray-brand-900">
        <button
          @click="showOptions = !showOptions"
          class="inline-flex items-center rounded-l-md border border-brand-800 bg-white group-hover:bg-brand-100 px-2 py-2"
        >
          <svg
            v-if="showOptions"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
          </svg>
          <svg
            v-if="!showOptions"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4 group-hover:hidden"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
            />
          </svg>
          <svg
            v-if="!showOptions"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4 hidden group-hover:block"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        <button
          @click="showOptions = !showOptions"
          data-testid="package-picker-toggle"
          class="-ml-px block w-full rounded-l-none rounded-r-md border-0 bg-white group-hover:bg-brand-100 py-1.5 pl-3 pr-3 ring-1 ring-inset ring-brand-800 focus:ring-2 focus:ring-inset focus:ring-brand-800 text-sm font-semibold items-center tracking-tight whitespace-nowrap"
        >
          {{ loading ? 'Updating package...' : selectedPackageName }}
        </button>
      </span>
    </div>

    <div v-show="showOptions" class="mt-4 grid grid-cols-1 gap-y-2 sm:grid-cols-3 sm:gap-x-4">
      <label
        v-for="pack in packages"
        :key="pack.id"
        :data-testid="`package-option-${pack.id}`"
        class="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
        :class="{
          'border-brand-600 ring-2 ring-brand-600': selectedPackageId === pack.id,
          'border-gray-300': selectedPackageId !== pack.id
        }"
        @click.prevent="setPackageID(pack.id)"
      >
        <input type="radio" v-model="selectedPackageId" :value="pack.id" class="sr-only" />
        <span class="flex flex-1">
          <span class="flex flex-col">
            <span class="block text-sm font-medium text-gray-900">
              {{ pack.name }}
            </span>
            <span v-if="pack.description" class="mt-1 flex items-center text-sm text-gray-500">
              {{ pack.description }}
            </span>
          </span>
        </span>
        <svg
          class="h-5 w-5 text-brand-600"
          :class="{ invisible: selectedPackageId !== pack.id }"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
            clip-rule="evenodd"
          />
        </svg>
        <span
          class="pointer-events-none absolute -inset-px rounded-lg border-2"
          :class="{
            'border border-brand-600': selectedPackageId === pack.id,
            'border-2 border-transparent': selectedPackageId !== pack.id
          }"
          aria-hidden="true"
        ></span>
      </label>
    </div>
  </div>
</template>

<script>
export default {
  name: 'V2PackageSelection',

  inject: ['v2SetPackageId', 'v2CartPackageId'],

  props: {
    packages: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      selectedPackageId: null,
      showOptions: false,
      loading: false
    }
  },

  computed: {
    selectedPackageName() {
      const selected = this.packages.find((p) => p.id === this.selectedPackageId)
      return selected?.name || 'Select a package'
    }
  },

  watch: {
    // Source of truth for V2 is the cached cart's package_id (persisted to
    // bottle.package_id by the V2 carts controller). immediate: true seeds
    // the initial selection on mount, so navigating Selection → Checkout →
    // Edit Cart restores the highlighted tile without a V1 Vuex read.
    v2CartPackageId: {
      immediate: true,
      handler(newId) {
        const next = newId ?? null
        if (next !== this.selectedPackageId) {
          this.selectedPackageId = next
        }
      }
    }
  },

  methods: {
    async setPackageID(packageId) {
      // Click-to-deselect: re-clicking the active package sends null so the
      // backend clears bottle.package_id and the cached cart records
      // package_id: null. Cart items stay (CartPricingService doesn't run
      // autofill when @package is nil).
      const nextPackageId = this.selectedPackageId === packageId ? null : packageId
      this.selectedPackageId = nextPackageId
      this.showOptions = false
      this.loading = true
      await this.v2SetPackageId(nextPackageId)
      this.loading = false
    }
  }
}
</script>
